import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, IconButton, Button
} from '@mui/material';
import {
  People,
  KeyboardArrowUp,
  OpenInNew
} from '@mui/icons-material';
import '../styles/Wiki.css';
import WikiClasses from './WikiClasses';

const TABS = [
  {
    label: 'Classes',
    icon: <People />,
    color: '#B67A3C',
    emoji: '⚔️',
    description: 'Découvrez toutes les classes jouables sur Nexus, leurs spécificités, leurs sorts et leurs rôles en combat.',
    placeholder: 'Les fiches de classes arrivent bientôt. Vous y trouverez les sorts, les builds recommandés et les rôles de chaque classe.'
  }
];

function TabPanel({ children, value, index }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      className={`wiki-tabpanel${value === index ? ' wiki-tabpanel--active' : ''}`}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

const Wiki = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const current = TABS[activeTab];

  return (
    <Box className="wiki-container">
      {/* ── Bouton scroll-to-top ── */}
      <IconButton
        onClick={scrollToTop}
        className={`wiki-scroll-top${showScrollTop ? '' : ' wiki-scroll-top--hidden'}`}
        aria-label="Remonter en haut"
      >
        <KeyboardArrowUp />
      </IconButton>

      {/* ── Hero Banner ── */}
      <Box className="wiki-hero">
        <div className="wiki-hero-particles" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', pt: 14, pb: 6 }}>
          <Typography
            variant="h2"
            className="wiki-hero-title"
            sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, color: '#fff', mb: 1.5 }}
          >
            📖 Wiki Nexus
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 400, maxWidth: 560, mx: 'auto' }}
          >
            Toutes les informations sur le serveur, rassemblées en un seul endroit.
          </Typography>
        </Container>
      </Box>

      {/* ── Tabs Bar & Bouton Externe ── */}
      <Box className="wiki-tabs-wrapper">
        <Container 
          maxWidth="lg" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 2 
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className="wiki-tabs"
            TabIndicatorProps={{ style: { background: current.color, height: 3, borderRadius: 2 } }}
            sx={{ minHeight: 56, flexGrow: 1 }}
          >
            {TABS.map((tab, i) => (
              <Tab
                key={tab.label}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                className={`wiki-tab${activeTab === i ? ' wiki-tab--active' : ''}`}
                sx={{
                  color: activeTab === i ? current.color : 'rgba(255,255,255,0.5)',
                  fontWeight: activeTab === i ? 700 : 400,
                  fontSize: '0.9rem',
                  minHeight: 56,
                  gap: 0.5,
                  transition: 'color 0.2s',
                  '& .MuiTab-iconWrapper': {
                    color: activeTab === i ? current.color : 'rgba(255,255,255,0.4)',
                  },
                  '&:hover': { color: '#fff' },
                }}
              />
            ))}
          </Tabs>

          {/* ── Bouton vers le nouveau Wiki HTML externe ── */}
          <Button
            variant="outlined"
            href="https://wiki.nexus-temporel.com"
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNew />}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.3)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: '#fff',
                background: 'rgba(255,255,255,0.08)'
              }
            }}
          >
            Consulter le Wiki Complet
          </Button>
        </Container>
      </Box>

      {/* ── Tab Panels ── */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {TABS.map((tab, i) => (
          <TabPanel key={tab.label} value={activeTab} index={i}>
            {/* Header de section */}
            <Box className="wiki-section-header" sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Box
                  className="wiki-section-icon"
                  sx={{ background: `${tab.color}22`, border: `1.5px solid ${tab.color}55`, color: tab.color }}
                >
                  {tab.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  {tab.emoji} {tab.label}
                </Typography>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', maxWidth: 720, lineHeight: 1.7 }}>
                {tab.description}
              </Typography>
            </Box>

            {/* Composant dédié aux classes */}
            <WikiClasses />
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};

export default Wiki;